/**
 * Payment service for Stripe integration
 */

import { apiGet, apiPost } from './api';
import { annualSavingPercent } from './planMath';

/**
 * Payment type - subscription or one-time donation
 */
export type PaymentType = 'subscription' | 'donation';

/**
 * Subscription plan
 */
export type SubscriptionPlan = 'monthly' | 'annual' | 'founding';

/**
 * Supported currencies
 */
export type Currency = 'gbp' | 'eur' | 'usd';

/**
 * Subscription status
 */
export type SubscriptionStatus =
  | 'active'
  | 'cancelled'
  | 'expired'
  | 'past_due'
  | 'incomplete'
  | 'trialing';

/**
 * Checkout session request
 */
export interface CheckoutSessionRequest {
  payment_type: PaymentType;
  currency: Currency;
  plan?: SubscriptionPlan;
  amount?: number;
  success_url?: string;
  cancel_url?: string;
}

/**
 * Checkout session response
 */
export interface CheckoutSessionResponse {
  session_id: string;
  url: string;
}

/**
 * Portal session response
 */
export interface PortalSessionResponse {
  url: string;
}

/**
 * Subscription info
 */
export interface Subscription {
  id: string;
  user_id: string;
  plan: string;
  status: SubscriptionStatus;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Subscription status response
 */
export interface SubscriptionStatusResponse {
  is_subscribed: boolean;
  subscription: Subscription | null;
  next_billing_date: string | null;
  can_manage: boolean;
  referral_premium_until: string | null;
  is_referral_premium: boolean;
  /** End of the new-account Premium welcome week, while it is running. */
  welcome_week_until?: string | null;
  /** Founding members only. */
  is_founding_member?: boolean;
  /** Founders Discord invite; sent to current founding members only, and only if set. */
  founders_discord_url?: string | null;
}

/**
 * Tier limits from system settings
 */
export interface TierLimits {
  queries_per_month?: number; // -1 = unlimited; the free plan's allowance
  queries_per_day: number; // -1 = unlimited
  max_armies: number; // -1 = unlimited
  max_active_battles: number; // -1 = unlimited
  max_collection_items?: number; // -1 = unlimited
  max_journal_photos?: number; // -1 = unlimited
  max_projects?: number; // -1 = unlimited
}

/**
 * Pricing tier information from backend
 */
export interface PricingTier {
  id: string;
  name: string;
  description: string;
  price_gbp: number;
  price_converted: number;
  period?: 'monthly' | 'annual' | 'one-time' | null;
  features: string[];
  limits?: TierLimits | null;
  is_popular?: boolean;
  is_coming_soon?: boolean;
  button_text: string;
  /** What a yearly plan works out to per month, in GBP. */
  monthly_equivalent_gbp?: number | null;
  /** Saving of the yearly plan against twelve monthly payments, computed by the backend. */
  saving_percent?: number | null;
  /** Founding Member only: places still open. */
  seats_remaining?: number | null;
}

/**
 * Exchange rates from GBP
 */
export interface ExchangeRates {
  gbp: number;
  usd: number;
  eur: number;
  last_updated?: string | null;
}

/**
 * Pricing response from backend
 */
export interface PricingResponse {
  tiers: PricingTier[];
  detected_currency: Currency;
  exchange_rates: ExchangeRates;
  currency_symbol: string;
  /** Checkout always charges this currency; other currencies are display-only estimates */
  charge_currency?: Currency;
  /** One line explaining "fair use", shown under the plans. */
  fair_use_note?: string;
  /** Length of the new-account Premium welcome week in days (0 = off). */
  welcome_week_days?: number;
}


/**
 * Fetch pricing tiers from backend
 * Returns dynamic pricing with exchange rates and limits from system settings
 */
export async function fetchPricingTiers(currency?: Currency): Promise<PricingResponse> {
  const params = currency ? `?currency=${currency}` : '';
  const response = await apiGet<PricingResponse>(`/api/pricing${params}`);

  if (response.error || !response.data) {
    // Return fallback pricing if API fails
    return getFallbackPricing();
  }

  return response.data;
}

/**
 * Fallback pricing if backend is unavailable
 */
function getFallbackPricing(): PricingResponse {
  const premiumFeatures = [
    'Unlimited AI questions (fair use)',
    'Unlimited rules library searches',
    'In-depth answers mid-battle and for list building',
    'Unlimited active battles',
    'Full battle history, kept forever',
    'Unlimited rosters',
    'Unlimited collection units (fair use)',
    'Unlimited painting journal photos',
    'Rewrite battle reports any time',
    'Supporter badge',
    "Help shape what's built next",
  ];
  const saving = annualSavingPercent(4.99, 39);
  return {
    tiers: [
      {
        id: 'free',
        name: 'Free',
        description: 'Free to play',
        price_gbp: 0,
        price_converted: 0,
        features: [
          '30 AI questions a month',
          'Unlimited rules library searches',
          '3 active battles at once',
          'Full battle history, kept forever',
          '25 rosters',
          '1000 collection units',
          '250 painting journal photos',
          'One battle report per game',
        ],
        button_text: 'Current Plan',
      },
      {
        id: 'annual',
        name: 'Premium Annual',
        description: `£3.25 a month, billed yearly (£39), save ${saving}%`,
        price_gbp: 39,
        price_converted: 39,
        period: 'annual',
        features: premiumFeatures,
        is_popular: true,
        button_text: 'Subscribe Yearly',
        monthly_equivalent_gbp: 3.25,
        saving_percent: saving,
      },
      {
        id: 'monthly',
        name: 'Premium Monthly',
        description: 'Full access, billed monthly',
        price_gbp: 4.99,
        price_converted: 4.99,
        period: 'monthly',
        features: premiumFeatures,
        button_text: 'Subscribe Monthly',
      },
      {
        id: 'coffee',
        name: 'Buy Us a Coffee',
        description: 'One-time donation with lifetime badge',
        price_gbp: 3.0,
        price_converted: 3.0,
        period: 'one-time',
        features: [
          'Supporter badge on profile',
          'Badge on your rosters',
          'Show your support',
          'No subscription required',
        ],
        button_text: 'Donate',
      },
    ],
    detected_currency: 'gbp',
    exchange_rates: { gbp: 1.0, usd: 1.27, eur: 1.17 },
    currency_symbol: '£',
    charge_currency: 'gbp',
    fair_use_note:
      "Fair use applies: it keeps one account from running up huge costs. Normal players won't notice it.",
    welcome_week_days: 7,
  };
}

/**
 * Get currency symbol
 */
export function getCurrencySymbol(currency: Currency): string {
  switch (currency) {
    case 'gbp':
      return '£';
    case 'usd':
      return '$';
    case 'eur':
      return '€';
    default:
      return '£';
  }
}

/**
 * Format price for display
 */
export function formatPrice(amount: number, currency: Currency): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Create a checkout session for subscription or donation
 */
export async function createCheckoutSession(request: CheckoutSessionRequest) {
  return apiPost<CheckoutSessionResponse>('/api/payments/checkout-session', request);
}

/**
 * Anonymous donation request
 */
export interface AnonymousDonationRequest {
  currency: Currency;
  amount: number;
  success_url?: string;
  cancel_url?: string;
}

/**
 * Create an anonymous donation checkout session (no account required)
 */
export async function createAnonymousDonation(request: AnonymousDonationRequest) {
  return apiPost<CheckoutSessionResponse>('/api/payments/donate', request);
}

/**
 * Create a customer portal session for subscription management
 */
export async function createPortalSession(returnUrl?: string) {
  return apiPost<PortalSessionResponse>('/api/payments/portal-session', {
    return_url: returnUrl,
  });
}

/**
 * Get current subscription status
 */
export async function getSubscriptionStatus() {
  return apiGet<SubscriptionStatusResponse>('/api/payments/subscription-status');
}

/**
 * Cancel subscription at period end
 */
export async function cancelSubscription() {
  return apiPost<{ message: string; cancel_date: string }>(
    '/api/payments/cancel-subscription'
  );
}

/**
 * Redirect to Stripe checkout
 */
export async function redirectToCheckout(
  paymentType: PaymentType,
  currency: Currency,
  plan?: SubscriptionPlan,
  amount?: number
): Promise<void> {
  const response = await createCheckoutSession({
    payment_type: paymentType,
    currency,
    plan,
    amount,
  });

  if (response.error || !response.data) {
    throw new Error(response.error?.message || 'Failed to create checkout session');
  }

  // Redirect to Stripe Checkout
  window.location.href = response.data.url;
}

/**
 * Redirect to Stripe billing portal
 */
export async function redirectToPortal(returnUrl?: string): Promise<void> {
  const response = await createPortalSession(returnUrl);

  if (response.error || !response.data) {
    throw new Error(response.error?.message || 'Failed to create portal session');
  }

  // Redirect to Stripe Portal
  window.location.href = response.data.url;
}

/**
 * Redirect to anonymous donation checkout (no account required)
 */
export async function redirectToAnonymousDonation(
  currency: Currency,
  amount: number = 3.00
): Promise<void> {
  const response = await createAnonymousDonation({
    currency,
    amount,
  });

  if (response.error || !response.data) {
    throw new Error(response.error?.message || 'Failed to create donation session');
  }

  // Redirect to Stripe Checkout
  window.location.href = response.data.url;
}
